import React, { useState, useContext, useEffect, ReactNode } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { ProductContext } from "../../../contexts/ProductContext";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomRadioGroup from "../../../components/Input/CustomRadioGroup";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomDatePicker from "../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { SampleType, WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { parseDate } from "../../../api/services/common/FormatDateService";
import InfoBox from "../../../components/InfoBox";
import ProductSampleGallery from "./ProductSampleGallery";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import HiddenError from "../../../models/HiddenError";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import ProductSide, { PRODUCT_SIDES, PRODUCT_SIDE_DICTIONARY } from "../models/ProductSide.model";
import {
  ProductSample,
  TesaProductSample,
  TesaProductSampleRequest,
} from "../../../api/models/product/ProductSampleReference";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginBottom: {
      marginBottom: theme.spacing(5),
    },
    marginTop: {
      marginTop: theme.spacing(4),
    },
  }),
);

type Deliverer = TestRequestRaw.Schemas.Deliverer;

interface ProductSampleDetailsForm {
  id?: number;
  claimedProduct: string | null;
  deliveredBy: Deliverer;
  descriptionOrNotes: string;
  lotNumber: string;
  manufacturingDate: Date | null;
  nart: string;
  productName: string;
  baseProduct: string;
  productSampleName: string;
  receivedOn: Date | null;
  sideOfProduct: ProductSide;
  source: string;
  attachments?: TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[];
}

interface ProductSampleDetailsDialogProps extends CustomDialogProps {
  testRequestID: string;
  callback: (closeDialog: boolean) => void;
  disabled?: boolean;
  productProperties?: { nart: string; productName: string; baseProduct: string };
}

const ProductSampleDetailsDialog: React.FC<ProductSampleDetailsDialogProps> = (
  props: ProductSampleDetailsDialogProps,
) => {
  const { callback, disabled, open, onClose, testRequestID: testRequestId, productProperties } = props;

  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductSamplesApi(api);

  const { productReference, productObject, updateProductObject } = useContext(ProductContext);

  const classes = useStyles(props);

  const [sampleName, setSampleName] = useState(
    productObject ? productObject.productSampleName : productReference.nart.substring(0, 5),
  );
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<ProductSampleDetailsForm | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);

  let formRef: FormikProps<ProductSampleDetailsForm> | null = null;

  const { productSamples, newProductSample } = React.useContext(TestRequestPermissionContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const isPredefined = !!productObject?.usedIn;
  const permissions = productObject ? productSamples?.[productObject.id] : newProductSample;
  const formFieldsDisabled = disabled || !permissions?.editDefinition;

  const inputFormatErrorMessage = "Invalid entry, please use only letters and numbers";
  const yupValidationSchema = Yup.object().shape({
    id: Yup.number(),
    claimedProduct: Yup.string().required("Please choose an option"),
    deliveredBy: Yup.string().required("Please choose an option"),
    descriptionOrNotes: Yup.string(),
    lotNumber: Yup.string(),
    nart: Yup.string(),
    productSampleName: Yup.string()
      .matches(/\w/, inputFormatErrorMessage)
      .required("Please provide a sample name")
      .test(
        "invalid character test",
        "Name cannot contain a hash '#' symbol",
        (value) => value && !value.includes("#"),
      ),
    receivedOn: Yup.string().nullable(),
    sideOfProduct: Yup.string().required("Please make a selection"),
    source: Yup.string(),
  });

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  useEffect(() => {
    setSampleName(productReference.nart.substring(0, 5));

    return (): void => {
      setSampleName("");
    };
  }, [productReference.nart]);

  useEffect(() => {
    if (productObject) {
      setSampleName(productObject.productSampleName);
    }
  }, [productObject]);

  const showGallery = open && (productObject || productObject?.id === 0) && testRequestId && productObject?.attachments;
  const showExtraSaveButton = !disabled && !showGallery && !productObject?.id && permissions?.editAttachments;
  const showGalleryAddButton = !disabled && showGallery && permissions?.editAttachments;

  const save = async (values: ProductSampleDetailsForm): Promise<ProductSample> => {
    const payload: TesaProductSample = {
      id: productObject?.id,
      claimedProduct: values.claimedProduct === "YES",
      deliveredBy: values.deliveredBy,
      descriptionOrNotes: values.descriptionOrNotes,
      lotNumber: values.lotNumber,
      manufacturingDate: values.manufacturingDate?.toISOString() as string,
      nart: values.nart,
      productName: values.productName,
      baseProduct: values.baseProduct,
      productSampleName: values.productSampleName,
      receivedOn: values.receivedOn?.toISOString() as string,
      sideOfProduct: values.sideOfProduct,
      source: values.source,
      type: "TESA_PRODUCT",
      attachments: values.attachments as TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[],
    };

    if (productObject) {
      const res = await productSamplesApi.updateProductSample(testRequestId, payload as TesaProductSample);
      showSnackbar("Product sample updated", "success");
      return res.data;
    }

    const productSampleResponse = await testRequestApi.createTesaProduct(payload as TesaProductSampleRequest);
    if (permissions?.editDefinition && testRequestId) {
      const newProductSampleId = Number(productSampleResponse.data.id);
      return productSamplesApi.updateTestRequestProducts(testRequestId, [newProductSampleId]).then(() => {
        showSnackbar("Product sample created", "success");
        onClose();
        return {
          ...productSampleResponse.data,
          attachments: [],
        };
      });
    }
    return productSampleResponse.data;
  };

  const update = (values: ProductSampleDetailsForm, actions: FormikHelpers<ProductSampleDetailsForm>): void => {
    if (disabled || !formRef?.dirty || !permissions?.editDefinition) {
      actions.resetForm();
      callback(true);
      onClose();
    } else {
      const fieldsToCheck = [values.productSampleName, values.source, values.descriptionOrNotes, values.lotNumber];

      if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
        setPendingValues(values);
        setShowConfidentialDialog(true);
        return;
      }

      save(values)
        .then(() => {
          actions.resetForm();
          callback(true);
          onClose();
        })
        .catch(handleHttpError);
    }
  };

  return (
    <>
      <ProductContext.Consumer>
        {() => {
          const productObjectClaimedValue = productObject?.claimedProduct ? "YES" : "NO";

          return (
            <CustomDialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
              <h2 className={classes.marginBottom}>
                {isPredefined ? (
                  <WithPredefinedChip
                    details={{
                      testRequestId: productObject.usedIn,
                      sampleType: SampleType.Product,
                    }}
                    spacing={2}
                    text={`tesa PRODUCT ${sampleName}`}
                  />
                ) : (
                  <>tesa PRODUCT {sampleName}</>
                )}
              </h2>
              <h3 className={[classes.marginBottom, classes.marginTop].join(" ")}>Product Sample Definition</h3>
              <Formik
                innerRef={(ref) => {
                  if (ref) {
                    formRef = ref;
                  }
                }}
                initialValues={{
                  claimedProduct: productObject ? productObjectClaimedValue : null,
                  deliveredBy: productObject?.deliveredBy || "",
                  descriptionOrNotes: productObject ? productObject.descriptionOrNotes : "",
                  lotNumber: productObject?.lotNumber || "",
                  manufacturingDate: parseDate(productObject?.manufacturingDate),
                  nart: productObject?.nart || productReference.nart || productProperties?.nart,
                  productName:
                    productObject?.productName || productProperties?.productName || productReference.productName,
                  baseProduct:
                    productObject?.baseProduct || productProperties?.baseProduct || productReference.baseProduct,
                  productSampleName: productObject
                    ? productObject.productSampleName
                    : productReference.nart.substring(0, 5),
                  receivedOn: parseDate(productObject?.receivedOn),
                  sideOfProduct: productObject ? productObject.sideOfProduct : "OPEN",
                  source: productObject?.source || "",
                }}
                onSubmit={update}
                validationSchema={yupValidationSchema}
              >
                {(formikProps: FormikProps<ProductSampleDetailsForm>): ReactNode => {
                  const { errors, isSubmitting, handleBlur, handleChange, setFieldValue, touched, values } =
                    formikProps;
                  return (
                    <Form>
                      <Box pb={4}>
                        <Grid container justify="flex-start" direction="row" spacing={4}>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <InfoBox text="Keep the sample name as short as possible.">
                              <CustomTextField
                                autoFocus
                                fullWidth
                                mandatory
                                disabled={formFieldsDisabled}
                                name="productSampleName"
                                id="productSampleName"
                                label="Sample Name"
                                value={values.productSampleName}
                                type="productSampleName"
                                helperText={[
                                  errors.productSampleName && touched.productSampleName ? errors.productSampleName : "",
                                ].join(" ")}
                                error={!!(errors.productSampleName && touched.productSampleName)}
                                onChange={(e): void => {
                                  handleChange(e);
                                  setSampleName(e.target.value);
                                }}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="nart"
                              id="nart"
                              label="NART"
                              value={values.nart}
                              type="nart"
                              helperText={[errors.nart && touched.nart ? errors.nart : ""].join(" ")}
                              error={!!(errors.nart && touched.nart)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="productName"
                              id="productName"
                              label="Product name"
                              value={values.productName}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="baseProduct"
                              id="baseProduct"
                              label="Base product"
                              value={values.baseProduct}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            {productObject && (
                              <CustomTextField
                                fullWidth
                                disabled
                                label="Product Sample ID"
                                value={values.id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              label="Received On"
                              value={values.receivedOn}
                              onChange={(date) => setFieldValue("receivedOn", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <InfoBox text="This can be the Customer, Requester or Plant.">
                              <CustomTextField
                                fullWidth
                                disabled={formFieldsDisabled}
                                name="source"
                                id="source"
                                label="Source"
                                value={values.source}
                                type="source"
                                helperText={[errors.source && touched.source ? errors.source : ""].join(" ")}
                                error={!!(errors.source && touched.source)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <InfoBox
                              compress
                              text="Indicate if the roll is part of a quality claim (this is critical)! If the roll belongs to a claim you can expect that the values, you are going to measure are not matching the standard performance of that product. Thus, the sample and all belonging results need to be flagged with ‘claimed’ (only for tesa Products)."
                            >
                              <CustomRadioGroup
                                disabled={formFieldsDisabled}
                                error={!!(errors.claimedProduct && touched.claimedProduct)}
                                formLabel="Claimed Product"
                                mandatory
                                name="claimedProduct"
                                options={["Yes", "No"]}
                                onChange={handleChange}
                                value={values.claimedProduct}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item xs={3}>
                            <InfoBox text="Indicate the side of the tape you want to test. If you want to test both sides of a tape, please create two product samples. One per side!">
                              <CustomDropdown
                                mandatory
                                disabled={formFieldsDisabled}
                                labelText="Side of the Product"
                                name="sideOfProduct"
                                id="sideOfProduct"
                                value={values.sideOfProduct}
                                type="sideOfProduct"
                                error={!!(errors.sideOfProduct && touched.sideOfProduct)}
                                onChange={handleChange}
                              >
                                {PRODUCT_SIDES.map((ps) => (
                                  <MenuItem key={ps} value={ps}>
                                    {PRODUCT_SIDE_DICTIONARY[ps] || ""}
                                  </MenuItem>
                                ))}
                              </CustomDropdown>
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomRadioGroup
                              disabled={formFieldsDisabled}
                              error={!!(errors.deliveredBy && touched.deliveredBy)}
                              formLabel="Delivered By"
                              mandatory
                              name="deliveredBy"
                              options={["CSC Lab", "Requester"]}
                              onChange={handleChange}
                              value={values.deliveredBy}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              helperText="When the roll was produced"
                              label="Manufacturing Date"
                              value={values.manufacturingDate}
                              onChange={(date) => setFieldValue("manufacturingDate", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="lotNumber"
                              id="lotNumber"
                              label="Lot Number"
                              value={values.lotNumber}
                              type="lotNumber"
                              helperText={[errors.lotNumber && touched.lotNumber ? errors.lotNumber : ""].join(" ")}
                              error={!!(errors.lotNumber && touched.lotNumber)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="descriptionOrNotes"
                              id="descriptionOrNotes"
                              label="Notes and Remarks"
                              value={values.descriptionOrNotes}
                              type="descriptionOrNotes"
                              helperText={[
                                errors.descriptionOrNotes && touched.descriptionOrNotes
                                  ? errors.descriptionOrNotes
                                  : "",
                              ].join(" ")}
                              error={!!(errors.descriptionOrNotes && touched.descriptionOrNotes)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box display="flex" flexDirection="row">
                        {showGalleryAddButton && (
                          <Box marginRight={2}>
                            <GalleryUploadButton hideIfNonEmpty label="Add Attachment" />
                          </Box>
                        )}
                        {showExtraSaveButton && (
                          <GalleryButton
                            icon="ADD"
                            text="Add Attachment"
                            onClick={() =>
                              showConfirmationDialog({
                                headline: "Save Product Sample",
                                text: "You have to save the Product Sample to add attachments.",
                                confirmText: "Save",
                              })
                                .then(() => {
                                  return formRef?.validateForm();
                                })
                                .then((formErrors) => {
                                  if (formErrors && Object.keys(formErrors).length > 0) {
                                    formRef?.submitForm();
                                    throw new HiddenError("form validation failed");
                                  }
                                  return formRef?.values;
                                })
                                .then((formValues) => {
                                  if (formValues) {
                                    return save(formValues).then((response) => {
                                      callback(false);
                                      formRef?.setFieldValue("id", response.id || null);
                                      updateProductObject(response);
                                      showSnackbar("Saved successfully.", "success");
                                    });
                                  }
                                })
                                .catch(handleHttpError)
                            }
                          />
                        )}
                      </Box>
                      {showGallery && (
                        <ProductSampleGallery
                          editable={(!disabled && permissions?.editAttachments) || false}
                          testRequestId={testRequestId}
                          productSampleId={productObject?.id}
                          productSampleName={sampleName}
                          productSampleAttachments={productObject?.attachments as ProductAttachment[]}
                          onChange={(attachments) => {
                            const existingAttachments = productObject?.attachments;
                            if (existingAttachments) {
                              existingAttachments.splice(0, existingAttachments.length, ...attachments);
                            }
                          }}
                        />
                      )}
                      <Grid container direction="row" justify="flex-end">
                        <CustomButton
                          disabled={isSubmitting}
                          primary
                          text={formFieldsDisabled ? "Close" : "Save and Close"}
                          type="submit"
                        />
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </CustomDialog>
          );
        }}
      </ProductContext.Consumer>
      {/* Confidential confirmation dialog */}
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          objectName="product sample"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues && testRequestId) {
              testRequestApi
                .updateConfidentiality(testRequestId, "HIGHLY_CONFIDENTIAL")
                .then(() => {
                  showSnackbar("Test request set to highly confidential", "success");
                  return save(pendingValues);
                })
                .then(() => {
                  formRef?.resetForm();
                  callback(true);
                  onClose();
                  showSnackbar("Product sample updated", "success");
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};

export default ProductSampleDetailsDialog;
